import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/layouts/PublicLayout";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { POLES, POLE_BY_ID } from "@/lib/poles";
import CompanyLogo from "@/components/CompanyLogo";
import { Search, MapPin, Tags, X as XIcon } from "lucide-react";
import { WT_TAG_CATEGORIES } from "@/lib/winetech_tags";

const COUNTRIES = ["all", "France", "Italy", "Spain", "Portugal", "USA"];

export default function MembersDirectory() {
  const { t, lang } = useLang();
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("all");
  const [pole, setPole] = useState("all");
  const [country, setCountry] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("name-asc");  // name-asc | name-desc | created_at-desc | created_at-asc
  // tagFilters: { tag_maturity: ["💡 Concept / Prototype", ...], ... }
  const [tagFilters, setTagFilters] = useState({});
  const [showTagPanel, setShowTagPanel] = useState(false);
  const qRef = useRef(q);
  useEffect(() => { qRef.current = q; }, [q]);

  // Load category list once
  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  const totalTags = Object.values(tagFilters).reduce((a, b) => a + (b?.length || 0), 0);

  const load = useCallback(() => {
    const [s, o] = sort.split("-");
    const params = { lang, sort: s, order: o };
    if (category !== "all") params.category = category;
    if (pole !== "all") params.pole = pole;
    if (country !== "all") params.country = country;
    if (qRef.current) params.q = qRef.current;
    // WineTech tag filters → comma-separated
    Object.entries(tagFilters).forEach(([k, list]) => {
      if (list && list.length > 0) params[k] = list.join(",");
    });
    api.get("/companies", { params }).then((r) => setCompanies(r.data)).catch(() => {});
  }, [category, pole, country, lang, sort, tagFilters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const tmo = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(tmo);
  }, [q, load]);

  const catLabel = (id) => categories.find((c) => c.id === id)?.name || id || "—";

  const toggleTag = (key, value) => {
    setTagFilters((prev) => {
      const cur = prev[key] || [];
      const next = cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value];
      return { ...prev, [key]: next };
    });
  };
  const clearTags = () => setTagFilters({});

  return (
    <PublicLayout>
      <Seo title="Members Directory" description="Browse the global directory of wine innovation members — startups, producers, technology providers, marketplaces and ecosystem partners." path="/members" />
      <section className="mx-auto max-w-7xl px-6 py-12">
        <div className="mb-8">
          <div className="h-1 w-12 rounded-full bg-wine-500" />
          <h1 className="mt-3 text-4xl font-bold text-navy md:text-5xl">{t("nav_members")}</h1>
          <p className="mt-3 max-w-2xl text-neutral-600">{t("members_directory_intro")}</p>
        </div>

        {/* Filters */}
        <div className="mb-8 grid gap-3 rounded-2xl border border-neutral-100 bg-white p-4 md:grid-cols-[1fr_auto_auto_auto_auto_auto]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder={t("search_placeholder")}
              className="w-full rounded-xl border border-neutral-200 bg-wine-50/30 py-2.5 pl-10 pr-3 text-sm outline-none focus:border-wine-300"
              data-testid="members-search-input"
            />
          </div>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm" data-testid="members-filter-category">
            <option value="all">{t("all_categories")}</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={pole} onChange={(e) => setPole(e.target.value)} className="rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm" data-testid="members-filter-pole">
            <option value="all">{t("all_poles") || "Tous les pôles"}</option>
            {POLES.map((p) => <option key={p.id} value={p.id}>{p.emoji} {p.label}</option>)}
          </select>
          <select value={country} onChange={(e) => setCountry(e.target.value)} className="rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm" data-testid="members-filter-country">
            {COUNTRIES.map((c) => <option key={c} value={c}>{c === "all" ? t("all_countries") : c}</option>)}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm" data-testid="members-sort">
            <option value="name-asc">Nom A → Z</option>
            <option value="name-desc">Nom Z → A</option>
            <option value="created_at-desc">{t("comp_membersdirectory_90")}</option>
            <option value="created_at-asc">Plus anciens d&apos;abord</option>
          </select>
          <button onClick={load} className="rounded-xl bg-wine-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-wine-600" data-testid="members-search-button">
            {t("search")}
          </button>
        </div>

        {/* WineTech multi-tag filters */}
        <div className="mb-8 rounded-2xl border border-neutral-100 bg-white p-4" data-testid="members-tag-filters">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => setShowTagPanel((v) => !v)}
              className="inline-flex items-center gap-2 text-sm font-bold text-navy hover:text-wine-700"
              data-testid="toggle-tag-panel"
            >
              <Tags className="h-4 w-4 text-wine-600" />
              Filtrer par tags d&apos;exploration
              {totalTags > 0 && (
                <span className="rounded-full bg-wine-500 px-2 py-0.5 text-[10px] font-bold text-white">{totalTags}</span>
              )}
              <span className="ml-1 text-xs text-neutral-400">{showTagPanel ? "−" : "+"}</span>
            </button>
            {totalTags > 0 && (
              <button type="button" onClick={clearTags} className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-3 py-1 text-[11px] font-semibold text-neutral-600 hover:bg-neutral-200" data-testid="clear-tag-filters">
                <XIcon className="h-3 w-3" /> Tout effacer
              </button>
            )}
          </div>
          {showTagPanel && (
            <div className="mt-4 space-y-4 border-t border-neutral-100 pt-4" data-testid="tag-filter-panel">
              {WT_TAG_CATEGORIES.map((cat) => {
                const selected = tagFilters[`tag_${cat.key}`] || [];
                return (
                  <div key={cat.key} data-testid={`tag-filter-cat-${cat.key}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-bold uppercase tracking-wider text-neutral-600">{cat.label}</div>
                      {selected.length > 0 && (
                        <span className="text-[10px] font-bold text-wine-600">{selected.length} sélectionné{selected.length > 1 ? "s" : ""}</span>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {cat.options.map((opt) => {
                        const isSel = selected.includes(opt);
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => toggleTag(`tag_${cat.key}`, opt)}
                            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                              isSel
                                ? `${cat.badge} font-bold ring-1 ring-current`
                                : "border-neutral-200 bg-white text-neutral-600 hover:border-wine-300 hover:bg-wine-50/40"
                            }`}
                            data-testid={`tag-filter-${cat.key}-${opt}`}
                            aria-pressed={isSel}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Grid */}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {companies.map((c, i) => {
            const p = POLE_BY_ID[c.pole];
            return (
              <Link
                key={c.id}
                to={`/members/${c.slug}`}
                className="group rounded-2xl border border-neutral-100 bg-white p-5 transition-all hover:-translate-y-1 hover:shadow-md"
                data-testid={`member-card-${i}`}
              >
                <div className="flex gap-4">
                  <CompanyLogo src={c.logo} name={c.name} size={56} className="rounded-xl" />
                  <div className="flex-1">
                    <h3 className="font-serif text-lg font-semibold text-neutral-900">{c.name}</h3>
                    <div className="mt-0.5 flex items-center gap-1 text-xs text-neutral-500">
                      <MapPin className="h-3 w-3" /> {c.city}, {c.country}
                    </div>
                  </div>
                </div>
                <p className="mt-3 line-clamp-3 text-sm text-neutral-600">{c.description}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {c.category && (
                    <span className="rounded-full bg-wine-50 px-2.5 py-1 text-xs font-semibold text-wine-700" data-testid={`member-category-${i}`}>{catLabel(c.category)}</span>
                  )}
                  {p && (
                    <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800" data-testid={`member-pole-${i}`}>{p.emoji} {p.label}</span>
                  )}
                  {c.tech_type && <span className="rounded-full bg-neutral-100 px-2.5 py-1 text-xs font-medium text-neutral-700">{c.tech_type}</span>}
                </div>
              </Link>
            );
          })}
          {companies.length === 0 && (
            <div className="col-span-full rounded-2xl border border-dashed border-neutral-200 bg-white p-12 text-center text-sm text-neutral-500" data-testid="members-empty">
              {t("no_members_found")}
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
